import {
  collection,
  doc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  serverTimestamp,
  onSnapshot,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import logger from '../utils/logger';

/**
 * System Notification Service
 * Handles app-wide notifications from administrators to all users
 * These are different from family notifications - they're global announcements
 */

/**
 * System notification types
 */
export const SYSTEM_NOTIFICATION_TYPES = {
  UPDATE: 'update',           // New features, bug fixes
  MAINTENANCE: 'maintenance', // Scheduled maintenance, outages
  TIP: 'tip',                // Tips and tricks
  ANNOUNCEMENT: 'announcement', // General announcements
  ALERT: 'alert'             // Important alerts
};

/**
 * System notification priority levels
 */
export const SYSTEM_PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent'
};

/**
 * Create a system notification (admin only)
 * @param {Object} notificationData - Notification data
 * @returns {Promise<string>} Notification ID
 */
export const createSystemNotification = async (notificationData) => {
  try {
    const {
      type,
      title,
      message,
      priority = SYSTEM_PRIORITY.MEDIUM,
      actionUrl = null,
      actionLabel = null,
      targetAudience = 'all', // 'all', 'specific', 'family_code'
      targetUsers = [], // Array of user IDs if targetAudience is 'specific'
      targetFamilyCodes = [], // Array of family codes if targetAudience is 'family_code'
      expiresAt = null, // Optional expiration date
      createdBy,
      createdByName
    } = notificationData;

    if (!type || !title || !message) {
      throw new Error('Missing required system notification fields');
    }

    if (!createdBy) {
      throw new Error('createdBy is required for system notifications');
    }

    const systemNotificationsRef = collection(db, 'systemNotifications');
    const notificationRef = await addDoc(systemNotificationsRef, {
      type,
      title,
      message,
      priority,
      actionUrl,
      actionLabel,
      targetAudience,
      targetUsers,
      targetFamilyCodes,
      expiresAt,
      createdBy,
      createdByName,
      isActive: true,
      createdAt: serverTimestamp()
    });

    return notificationRef.id;
  } catch (error) {
    logger.error('Error creating system notification:', error);
    throw error;
  }
};

/**
 * Get system notifications for a specific user
 * Checks if user should see the notification based on targetAudience
 * @param {string} userId - User ID
 * @param {string} familyCode - User's family code (optional)
 * @param {number} maxResults - Maximum number of notifications to fetch
 * @returns {Promise<Array>} Array of system notifications
 */
export const getSystemNotifications = async (userId, familyCode = null, maxResults = 50) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const systemNotificationsRef = collection(db, 'systemNotifications');

    // Get all active system notifications
    const q = query(
      systemNotificationsRef,
      where('isActive', '==', true),
      orderBy('createdAt', 'desc'),
      limit(maxResults)
    );

    const snapshot = await getDocs(q);
    const notifications = [];

    const now = new Date();

    snapshot.forEach(doc => {
      const data = doc.data();

      // Check if notification has expired
      if (data.expiresAt && data.expiresAt.toDate() < now) {
        return; // Skip expired notifications
      }

      // Filter based on targetAudience
      let shouldInclude = false;

      if (data.targetAudience === 'all') {
        shouldInclude = true;
      } else if (data.targetAudience === 'specific') {
        shouldInclude = data.targetUsers?.includes(userId);
      } else if (data.targetAudience === 'family_code' && familyCode) {
        shouldInclude = data.targetFamilyCodes?.includes(familyCode);
      }

      if (shouldInclude) {
        notifications.push({
          id: doc.id,
          ...data,
          isSystemNotification: true
        });
      }
    });

    return notifications;
  } catch (error) {
    logger.error('Error fetching system notifications:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time system notifications for a user
 * @param {string} userId - User ID
 * @param {string} familyCode - User's family code (optional)
 * @param {Function} onUpdate - Callback function when notifications update
 * @returns {Function} Unsubscribe function
 */
export const subscribeToSystemNotifications = (userId, familyCode, onUpdate) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const systemNotificationsRef = collection(db, 'systemNotifications');
    const q = query(
      systemNotificationsRef,
      where('isActive', '==', true),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notifications = [];
        const now = new Date();

        snapshot.forEach(doc => {
          const data = doc.data();

          // Check if notification has expired
          if (data.expiresAt && data.expiresAt.toDate() < now) {
            return;
          }

          // Filter based on targetAudience
          let shouldInclude = false;

          if (data.targetAudience === 'all') {
            shouldInclude = true;
          } else if (data.targetAudience === 'specific') {
            shouldInclude = data.targetUsers?.includes(userId);
          } else if (data.targetAudience === 'family_code' && familyCode) {
            shouldInclude = data.targetFamilyCodes?.includes(familyCode);
          }

          if (shouldInclude) {
            notifications.push({
              id: doc.id,
              ...data,
              isSystemNotification: true
            });
          }
        });

        onUpdate(notifications);
      },
      (error) => {
        logger.error('Error in system notification subscription:', error);
        onUpdate([]);
      }
    );

    return unsubscribe;
  } catch (error) {
    logger.error('Error subscribing to system notifications:', error);
    return () => {};
  }
};

/**
 * Mark a system notification as read for a specific user
 * This creates a read receipt in a subcollection
 * @param {string} notificationId - System notification ID
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export const markSystemNotificationAsRead = async (notificationId, userId) => {
  try {
    if (!notificationId || !userId) {
      throw new Error('Notification ID and user ID are required');
    }

    // Create a read receipt document using addDoc for auto-generated ID
    await addDoc(collection(db, 'systemNotifications', notificationId, 'readReceipts'), {
      userId,
      readAt: serverTimestamp()
    });

  } catch (error) {
    logger.error('Error marking system notification as read:', error);
    throw error;
  }
};

/**
 * Deactivate a system notification (admin only)
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export const deactivateSystemNotification = async (notificationId) => {
  try {
    if (!notificationId) {
      throw new Error('Notification ID is required');
    }

    const notificationRef = doc(db, 'systemNotifications', notificationId);
    await updateDoc(notificationRef, {
      isActive: false,
      deactivatedAt: serverTimestamp()
    });

  } catch (error) {
    logger.error('Error deactivating system notification:', error);
    throw error;
  }
};

/**
 * Delete a system notification (admin only)
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export const deleteSystemNotification = async (notificationId) => {
  try {
    if (!notificationId) {
      throw new Error('Notification ID is required');
    }

    const notificationRef = doc(db, 'systemNotifications', notificationId);
    await deleteDoc(notificationRef);

  } catch (error) {
    logger.error('Error deleting system notification:', error);
    throw error;
  }
};

/**
 * Get all system notifications (admin only)
 * @param {boolean} includeInactive - Include inactive notifications
 * @param {number} maxResults - Maximum number of notifications to fetch
 * @returns {Promise<Array>} Array of all system notifications
 */
export const getAllSystemNotifications = async (includeInactive = false, maxResults = 100) => {
  try {
    const systemNotificationsRef = collection(db, 'systemNotifications');

    let q;
    if (includeInactive) {
      q = query(
        systemNotificationsRef,
        orderBy('createdAt', 'desc'),
        limit(maxResults)
      );
    } else {
      q = query(
        systemNotificationsRef,
        where('isActive', '==', true),
        orderBy('createdAt', 'desc'),
        limit(maxResults)
      );
    }

    const snapshot = await getDocs(q);
    const notifications = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return notifications;
  } catch (error) {
    logger.error('Error fetching all system notifications:', error);
    throw error;
  }
};

const systemNotificationService = {
  createSystemNotification,
  getSystemNotifications,
  subscribeToSystemNotifications,
  markSystemNotificationAsRead,
  deactivateSystemNotification,
  deleteSystemNotification,
  getAllSystemNotifications,
  SYSTEM_NOTIFICATION_TYPES,
  SYSTEM_PRIORITY
};

export default systemNotificationService;
